import { Form, Input, Modal, Select } from 'antd'
import React, { useEffect } from 'react'
import { useQuery } from 'react-admin'

export interface HandleModalSubmit {
    (selectedService: string, serviceName: string): void;
}

/**
 * A new service item: which service, and what to call it.
 *
 * The form is the only place the answers live — the component used to keep a
 * `useState` per field alongside them, so a validated submit and the values it
 * submitted could disagree.
 */
const AddServiceItemModal = ({ modalVisible, setModalVisible, handleModalSubmit, existingNamesByService = {} }: { modalVisible: boolean, setModalVisible: Function, handleModalSubmit: HandleModalSubmit, existingNamesByService?: Record<string, string[]> }) => {
    const [form] = Form.useForm();

    useEffect(() => {
        if (modalVisible) form.resetFields();
    }, [modalVisible, form]);

    const { data, loading } = useQuery({
        type: "getList",
        resource: 'browse_services',
        payload: {
            pagination: { page: 1, perPage: 500 },
            sort: { field: "name", order: "asc" },
            filter: {}
        },
    });

    const onFinish = ({ serviceName, serviceItemName }: { serviceName: string, serviceItemName: string }) => {
        setModalVisible(false);
        handleModalSubmit(serviceName, serviceItemName.trim());
    };

    return (
        <Modal
            title="Add service item"
            open={modalVisible}
            okText="Add"
            onOk={() => form.submit()}
            onCancel={() => setModalVisible(false)}
            destroyOnClose
        >
            <Form form={form} layout="vertical" onFinish={onFinish} preserve={false}>
                {/* the name has to be unique within its service, so choosing a
                    different service re-asks the question */}
                <Form.Item
                    name="serviceItemName"
                    label="Service item name"
                    dependencies={['serviceName']}
                    validateTrigger={['onBlur', 'onSubmit']}
                    rules={[
                        { required: true, whitespace: true, message: 'Please enter a service item name' },
                        ({ getFieldValue }) => ({
                            // a clash is caught here rather than by silently
                            // overwriting the item that already has the name
                            validator: (_, value) => {
                                const service = getFieldValue('serviceName');
                                const taken = service ? existingNamesByService[service] ?? [] : [];
                                return value && taken.includes(value.trim())
                                    ? Promise.reject(new Error(`"${value.trim()}" already exists under ${service}`))
                                    : Promise.resolve();
                            },
                        }),
                    ]}
                >
                    <Input autoFocus placeholder="e.g. prod-db" onPressEnter={() => form.submit()} />
                </Form.Item>

                <Form.Item
                    name="serviceName"
                    label="Service"
                    rules={[{ required: true, message: 'Please select a service' }]}
                >
                    <Select
                        placeholder="Select service"
                        loading={loading}
                        /* the catalogue runs to hundreds; typing to narrow it is
                           faster than scrolling, and is the only way in from the
                           keyboard that does not take hundreds of key presses */
                        showSearch
                        optionFilterProp="label"
                        options={(data ?? []).map((service: any) => ({
                            value: service.name,
                            label: service.name,
                        }))}
                    />
                </Form.Item>
            </Form>
        </Modal>
    )
}

export default AddServiceItemModal
